import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ErrorAlert } from '../../components/ErrorAlert';
import { useMyPreferences, useUpdatePreferences } from '../../api/queries';
import { DEFAULT_SAS_COLORS } from './lineModel';
import type { SasColorRole, SasModelLine } from './lineModel';
import { isValidHexColor, mergeSasAnalyzerColors } from './colorPreferences';
import DecodeStream from './DecodeStream';

export type ColorsDialogProps = {
  open: boolean;
  onClose: () => void;
  /** The currently-applied palette (defaults already filled in by
   * `resolveSasAnalyzerColors` on the caller's side) — what the dialog
   * seeds its pending edits from each time it opens. */
  colors: Record<SasColorRole, string>;
};

const ROLE_ORDER: SasColorRole[] = ['tx', 'rx', 'text', 'frame', 'dim', 'ok', 'err'];

const ROLE_LABELS: Record<SasColorRole, string> = {
  tx: 'TX (transmit)',
  rx: 'RX (receive)',
  text: 'General text',
  frame: 'Frame / borders',
  dim: 'Dim annotations',
  ok: 'OK / good',
  err: 'Error',
};

// A handful of sample rows covering every one of the 7 roles, built with the
// same SasModelLine shape the real decode stream + .txt exporter use — the
// preview is rendered through the actual DecodeStream component so it's a
// faithful "what you'll see" rather than a hand-rolled colour swatch strip.
const PREVIEW_LINES: SasModelLine[] = [
  [{ text: '┌─ Preview ──────────────────────────┐', role: 'frame' }],
  [{ text: '│ ', role: 'frame' }, { text: '21:15:44.123  TX  01 1C', role: 'tx' }],
  [{ text: '│ ', role: 'frame' }, { text: '21:15:44.180  RX  01 1C 00 00 12 34', role: 'rx' }],
  [
    { text: '│              ', role: 'frame' },
    { text: 'Send meters response · CRC ', role: 'text' },
    { text: 'OK', role: 'ok' },
  ],
  [
    { text: '│              ', role: 'frame' },
    { text: 'Coin In                        ', role: 'text' },
    { text: '1,234', role: 'ok' },
  ],
  [
    { text: '│              ', role: 'frame' },
    { text: 'Reserved                       ', role: 'text' },
    { text: 'set        ', role: 'ok' },
    { text: 'bit 3', role: 'dim' },
  ],
  [{ text: '│              ', role: 'frame' }, { text: 'CRC BAD', role: 'err' }],
  [{ text: '└─────────────────────────────────────┘', role: 'frame' }],
];

/**
 * "Colours" dialog — per-user palette for the 7 decode-stream colour roles.
 * Role SEMANTICS are fixed in the renderer (lineModel.ts / DecodeStream.tsx
 * decide what gets the `err` vs. `ok` role); this dialog only lets the user
 * change which hex each role maps to.
 *
 * Persistence: `GET/PUT /api/v1/me/preferences` stores one JSON blob per
 * user and PUT REPLACES it whole (MeEndpoints.cs), so Save reads the
 * current blob (via `useMyPreferences`, already cached by the page) and
 * merges the edited `sasAnalyzer.colors` onto it (`mergeSasAnalyzerColors`)
 * before writing — every other preference key (theme mode, brand colours…)
 * survives untouched.
 */
export default function ColorsDialog({ open, onClose, colors }: ColorsDialogProps) {
  const prefsQ = useMyPreferences();
  const update = useUpdatePreferences();

  const [draft, setDraft] = useState<Record<SasColorRole, string>>(colors);

  // Reset the pending edit to the current palette only on the false->true
  // open transition — NOT on every render where `colors` happens to change
  // reference — so a background refetch while the dialog is open can't
  // stomp on what the user is mid-typing.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setDraft(colors);
    }
    wasOpenRef.current = open;
  }, [open, colors]);

  const allValid = ROLE_ORDER.every((role) => isValidHexColor(draft[role]));

  // Save must never fire `mergeSasAnalyzerColors(undefined, draft)` — that
  // merges the new palette onto `{}` instead of the real stored blob, which
  // silently WIPES every other preference key (theme mode, brand colours…)
  // once the PUT lands. `prefsQ.isLoading` alone isn't enough: once the GET
  // has settled into an error (or, defensively, any other state where
  // `data` never arrived), Save must stay disabled rather than assume "no
  // data" means "empty preferences".
  const prefsUnavailable = !prefsQ.isLoading && (prefsQ.isError || prefsQ.data === undefined);

  // Feed the preview only currently-valid colours; a role mid-edit with bad
  // text falls back to its default rather than handing DecodeStream a
  // non-colour string as a CSS custom-property value.
  const previewColors = useMemo(() => {
    const out: Record<SasColorRole, string> = { ...DEFAULT_SAS_COLORS };
    for (const role of ROLE_ORDER) {
      if (isValidHexColor(draft[role])) out[role] = draft[role].trim();
    }
    return out;
  }, [draft]);

  const setField = (role: SasColorRole, value: string) => {
    setDraft((prev) => ({ ...prev, [role]: value }));
  };

  const onReset = () => setDraft({ ...DEFAULT_SAS_COLORS });

  const onSave = () => {
    if (!allValid || prefsUnavailable) return;
    // Trim before persisting — the preview already renders trimmed values
    // (isValidHexColor/previewColors both trim), so an untrimmed save would
    // silently diverge from what the user saw in the preview strip.
    const trimmed = ROLE_ORDER.reduce((acc, role) => {
      acc[role] = draft[role].trim();
      return acc;
    }, {} as Record<SasColorRole, string>);
    const merged = mergeSasAnalyzerColors(prefsQ.data, trimmed);
    update.mutate(merged, { onSuccess: () => onClose() });
  };

  const busy = update.isPending;
  const error = update.error as { message?: string } | null;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        Analyser colours
        <IconButton size="small" onClick={onClose} disabled={busy}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Pick the colour used for each role in the decode stream. Roles are fixed — the error colour is
            only ever used for errors and the OK colour only for OK status / decoded values — but you choose
            which hex each one is.
          </Typography>

          {error?.message && <ErrorAlert>{error.message}</ErrorAlert>}
          {prefsUnavailable && (
            <ErrorAlert severity="warning">
              Preferences unavailable — retry. Saving is disabled in the meantime so your other
              saved preferences can't be overwritten with a blank value.
            </ErrorAlert>
          )}

          <Stack spacing={1.5}>
            {ROLE_ORDER.map((role) => {
              const value = draft[role];
              const valid = isValidHexColor(value);
              const label = ROLE_LABELS[role];
              return (
                <Stack key={role} direction="row" spacing={1.5} alignItems="center">
                  <Box
                    aria-hidden
                    sx={{
                      width: 32,
                      height: 32,
                      flexShrink: 0,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: valid ? value : 'transparent',
                      backgroundImage: valid
                        ? undefined
                        : 'repeating-linear-gradient(45deg, #ddd 0 4px, transparent 4px 8px)',
                    }}
                  />
                  <Box
                    component="input"
                    type="color"
                    aria-label={`Pick ${label} colour`}
                    value={valid ? value : DEFAULT_SAS_COLORS[role]}
                    onChange={(e) => setField(role, (e.target as HTMLInputElement).value)}
                    disabled={busy}
                    sx={{
                      width: 32, height: 32, flexShrink: 0, padding: 0,
                      border: 'none', background: 'none', cursor: busy ? 'default' : 'pointer',
                    }}
                  />
                  <TextField
                    label={label}
                    value={value}
                    onChange={(e) => setField(role, e.target.value)}
                    error={!valid}
                    helperText={valid ? ' ' : 'Enter a valid 6-digit hex colour, e.g. #ff9e4a.'}
                    size="small"
                    disabled={busy}
                    sx={{ flex: 1 }}
                    slotProps={{ htmlInput: { sx: { fontFamily: 'monospace' } } }}
                  />
                </Stack>
              );
            })}
          </Stack>

          <Typography variant="overline" color="text.secondary">Preview</Typography>
          <DecodeStream lines={PREVIEW_LINES} colors={previewColors} testId="sas-colors-preview" />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={onReset} disabled={busy}>Reset to default</Button>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            variant="contained"
            onClick={onSave}
            disabled={!allValid || busy || prefsQ.isLoading || prefsUnavailable}
          >
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
